/**
 * Product buy box
 * ---------------------------------------------------------------------------
 * Variant switching, quantity, personalisation, gift wrap, share and the
 * pincode estimate.
 *
 * The markup is Shopify's own `{% form 'product' %}`, so with JavaScript off
 * the page still posts normally and adds to the cart. With JavaScript on, the
 * submit is intercepted so the add happens in place and the cart drawer opens
 * over the page instead of navigating away to /cart.
 */

import { addToCart } from '@theme/wb-cart-add';

function money(cents) {
  const amount = cents / 100;
  const currency = window.Shopify?.currency?.active || 'INR';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

class ProductBuyBox {
  constructor(root) {
    this.root = root;
    this.section = root.closest('.wb-pdp');

    const data = this.section?.querySelector('[data-pdp-variants]');
    try {
      this.variants = data ? JSON.parse(data.textContent) : [];
    } catch {
      this.variants = [];
    }

    this.form = root.querySelector('#wb-product-form');
    this.idInput = root.querySelector('[data-pdp-variant-id]');
    this.priceEl = root.querySelector('[data-pdp-price]');
    this.compareEl = root.querySelector('[data-pdp-compare]');
    this.offEl = root.querySelector('[data-pdp-off]');
    this.stockEl = root.querySelector('[data-pdp-stock]');
    this.addBtn = root.querySelector('[data-pdp-add]');
    this.addLabel = root.querySelector('[data-pdp-add-label]');
    this.optionValueEl = root.querySelector('[data-pdp-option-value]');

    this.personaliseVariant = root.dataset.personaliseVariant;
    this.giftwrapVariant = root.dataset.giftwrapVariant;
    this.personalisePrice = Number(root.dataset.personalisePrice || 0);
    this.giftwrapPrice = Number(root.dataset.giftwrapPrice || 0);
    // Shown on the add-on line in the cart, so it is obvious which product
    // the personalisation or wrap belongs to.
    this.productTitle = root.querySelector('.wb-pdp__title')?.textContent?.trim() || '';

    this.wireOptions();
    this.wireQuantity();
    this.wireSubmit();
    this.wirePersonalise();
    this.wireGiftWrap();
    this.wireShare();
    this.wirePincode();
    this.updateTotal();
  }

  /* ---- Variant selection -------------------------------------------------- */

  selectedOptions() {
    const inputs = Array.from(this.root.querySelectorAll('[data-pdp-option]:checked'));
    const options = [];
    inputs.forEach((input) => {
      // position is 1-based in Liquid.
      options[Number(input.dataset.pdpOption) - 1] = input.value;
    });
    return options;
  }

  wireOptions() {
    this.root.querySelectorAll('[data-pdp-option]').forEach((input) => {
      input.addEventListener('change', () => {
        // Move the selected styling to the new choice.
        const group = input.closest('.wb-pdp__swatches, .wb-pdp__pills');
        group?.querySelectorAll('.wb-pdp__swatch, .wb-pdp__pill').forEach((el) => {
          el.classList.toggle('is-selected', el.contains(input));
        });

        this.applyVariant();
      });
    });
  }

  applyVariant() {
    const chosen = this.selectedOptions();
    const variant = this.variants.find((v) =>
      v.options.every((opt, i) => chosen[i] === undefined || opt === chosen[i])
    );

    if (!variant) {
      this.setUnavailable();
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;
    if (this.priceEl) this.priceEl.textContent = money(variant.price);
    this.updateTotal();

    const onSale = variant.compare_at_price > variant.price;
    if (this.compareEl) {
      this.compareEl.textContent = onSale ? money(variant.compare_at_price) : '';
      this.compareEl.hidden = !onSale;
    }
    if (this.offEl) {
      const pct = onSale
        ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
        : 0;
      this.offEl.textContent = `${pct}% OFF`;
      this.offEl.hidden = !onSale;
    }

    if (this.optionValueEl) {
      const colourIndex = this.variants[0]?.options?.length ? 0 : -1;
      if (colourIndex >= 0) this.optionValueEl.textContent = variant.options[0];
    }

    if (this.addBtn) this.addBtn.disabled = !variant.available;
    if (this.addLabel) {
      this.addLabel.textContent = variant.available
        ? this.addLabel.dataset.inStock || 'Add to cart'
        : 'Sold out';
    }

    if (this.stockEl) {
      this.stockEl.innerHTML = variant.available
        ? '<span class="wb-pdp__dot"></span> In stock — ready to ship'
        : '<span class="wb-pdp__dot wb-pdp__dot--out"></span> Sold out';
    }

    // Keep the URL shareable and the back button meaningful.
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // Let the gallery jump to this variant's image.
    document.dispatchEvent(
      new CustomEvent('variant:change', { detail: { variant } })
    );
  }

  setUnavailable() {
    if (this.addBtn) this.addBtn.disabled = true;
    if (this.addLabel) this.addLabel.textContent = 'Unavailable';
    if (this.stockEl) {
      this.stockEl.innerHTML =
        '<span class="wb-pdp__dot wb-pdp__dot--out"></span> This combination is unavailable';
    }
  }

  /* ---- Add to cart ---------------------------------------------------------
   * Add-ons are separate products, so a single form POST cannot carry them.
   * The submit is intercepted and posted as one /cart/add.js call containing
   * the item plus whichever add-ons are switched on — a single request, so
   * they either all land in the cart or none do.
   *
   * With JavaScript off the browser posts the form normally and the main item
   * still reaches the cart; only the add-ons are skipped.
   */

  /** Current variant price times quantity, plus any ticked add-ons. */
  orderTotal() {
    const qty = Number(this.root.querySelector('[data-pdp-qty-input]')?.value || 1);
    const variant = this.variants.find((v) => String(v.id) === String(this.idInput?.value));
    let total = (variant?.price || 0) * qty;

    if (this.root.querySelector('[data-pdp-personalise]')?.checked) {
      total += this.personalisePrice * qty;
    }
    if (this.root.querySelector('[data-pdp-giftwrap]')?.checked) {
      total += this.giftwrapPrice;
    }
    return total;
  }

  updateTotal() {
    const el = this.root.querySelector('[data-pdp-add-total]');
    if (!el) return;

    const anyAddon =
      this.root.querySelector('[data-pdp-personalise]')?.checked ||
      this.root.querySelector('[data-pdp-giftwrap]')?.checked;

    // Only worth showing once it differs from the price above the button.
    el.textContent = anyAddon ? ` · ${money(this.orderTotal())}` : '';
  }

  /**
   * Line item properties carried by the main product — the monogram, the gift
   * wrap flag, and anything else rendered as a `properties[...]` input.
   * Disabled and empty inputs are skipped so blank properties never reach the
   * order.
   */
  mainProperties() {
    const properties = {};

    this.form?.querySelectorAll('[name^="properties["]').forEach((input) => {
      if (input.disabled) return;
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;

      const name = input.name.slice('properties['.length, -1);
      const value = input.value.trim();
      if (name && value) properties[name] = value;
    });

    return properties;
  }

  wireSubmit() {
    if (!this.form) return;

    this.form.addEventListener('submit', async (event) => {
      // The dynamic checkout button submits this same form and must be left
      // alone — it goes straight to checkout rather than to the cart.
      if (event.submitter && event.submitter !== this.addBtn) return;

      // The whole add — main product and add-ons together — goes through
      // fetch so the cart drawer opens over the page. A native form post would
      // navigate to /cart instead, which is what it used to do.
      event.preventDefault();

      const wantsPersonalise = this.root.querySelector('[data-pdp-personalise]')?.checked === true;
      const wantsGiftWrap = this.root.querySelector('[data-pdp-giftwrap]')?.checked === true;
      const qty = Number(this.root.querySelector('[data-pdp-qty-input]')?.value || 1);
      const monogram = this.root.querySelector('[data-pdp-monogram]')?.value?.trim();
      const variantId = this.idInput?.value || this.form.querySelector('[name="id"]')?.value;

      if (!variantId) return;

      const items = [{ id: Number(variantId), quantity: qty, properties: this.mainProperties() }];

      if (wantsPersonalise && this.personaliseVariant) {
        items.push({
          id: Number(this.personaliseVariant),
          quantity: qty,
          properties: monogram ? { Monogram: monogram, For: this.productTitle } : { For: this.productTitle },
        });
      }
      if (wantsGiftWrap && this.giftwrapVariant) {
        items.push({
          id: Number(this.giftwrapVariant),
          quantity: 1,
          properties: { For: this.productTitle },
        });
      }

      const button = this.addBtn;
      const label = this.addLabel;
      const original = label?.textContent;
      if (button) button.disabled = true;
      if (label) label.textContent = 'Adding…';

      try {
        await addToCart(items, { source: button });
        if (label) label.textContent = 'Added ✓';
      } catch (error) {
        if (label) label.textContent = error.message || 'Try again';
      } finally {
        setTimeout(() => {
          if (button) button.disabled = false;
          if (label && original) label.textContent = original;
        }, 1600);
      }
    });
  }

  /* ---- Quantity ----------------------------------------------------------- */

  wireQuantity() {
    const input = this.root.querySelector('[data-pdp-qty-input]');
    if (!input) return;

    this.root.querySelectorAll('[data-pdp-qty]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.dataset.pdpQty === 'up' ? 1 : -1;
        input.value = Math.max(1, Number(input.value) + step);
        this.updateTotal();
      });
    });

    input.addEventListener('change', () => this.updateTotal());
  }

  /* ---- Personalisation -----------------------------------------------------
   * The monogram input stays disabled until the option is on, so an empty
   * property is never posted with the line item.
   */

  wirePersonalise() {
    const toggle = this.root.querySelector('[data-pdp-personalise]');
    const field = this.root.querySelector('[data-pdp-personalise-field]');
    const input = this.root.querySelector('[data-pdp-monogram]');
    if (!toggle || !field || !input) return;

    toggle.addEventListener('change', () => {
      const on = toggle.checked;
      field.hidden = !on;
      input.disabled = !on;
      if (on) input.focus();
      else input.value = '';
      this.updateTotal();
    });
  }

  /* ---- Gift wrap ---------------------------------------------------------- */

  wireGiftWrap() {
    const toggle = this.root.querySelector('[data-pdp-giftwrap]');
    const hidden = this.root.querySelector('[data-pdp-giftwrap-input]');
    if (!toggle || !hidden) return;

    toggle.addEventListener('change', () => {
      hidden.value = toggle.checked ? 'Yes' : '';
      this.updateTotal();
    });
  }

  /* ---- Share -------------------------------------------------------------- */

  wireShare() {
    const button = this.root.querySelector('[data-pdp-share]');
    if (!button) return;

    button.addEventListener('click', async () => {
      const label = button.querySelector('span');
      const original = label?.textContent;
      const url = window.location.href;

      // Native share sheet where available, clipboard everywhere else.
      if (navigator.share) {
        try {
          await navigator.share({ title: document.title, url });
          return;
        } catch {
          /* Cancelled — fall through to copy. */
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        if (label) label.textContent = 'Link copied';
      } catch {
        if (label) label.textContent = 'Press Ctrl+C';
      }

      setTimeout(() => {
        if (label && original) label.textContent = original;
      }, 2000);
    });
  }

  /* ---- Pincode -------------------------------------------------------------
   * Resolves the pincode against India Post's public API (CORS-open, no key)
   * so the shopper sees a real place name rather than a bare format check.
   *
   * Wildebeast couriers pan-India, so any valid Indian pincode is
   * serviceable — the lookup confirms the pincode EXISTS and names the
   * district, which is what actually reassures people. It never claims to
   * check courier coverage, because nothing here can.
   */

  wirePincode() {
    const input = this.root.querySelector('[data-pdp-pin]');
    const button = this.root.querySelector('[data-pdp-pin-check]');
    const message = this.root.querySelector('[data-pdp-pin-msg]');
    if (!input || !button || !message) return;

    const setMsg = (text, state) => {
      message.textContent = text;
      message.className = `wb-pdp__pin-msg${state ? ` is-${state}` : ''}`;
    };

    const eta = (days) => {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
      });
    };

    const check = async () => {
      const pin = input.value.trim();

      // Indian pincodes never start with 0 or 9.
      if (!/^[1-8]\d{5}$/.test(pin)) {
        setMsg('Enter a valid 6-digit Indian pincode.', 'bad');
        return;
      }

      const days = Number(input.dataset.days || 4);
      button.disabled = true;
      setMsg('Checking…', '');

      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('lookup failed');

        const data = await response.json();
        const result = Array.isArray(data) ? data[0] : null;
        const office = result?.PostOffice?.[0];

        if (result?.Status !== 'Success' || !office) {
          setMsg('That pincode was not found. Please check and try again.', 'bad');
          return;
        }

        const place = [office.District, office.State].filter(Boolean).join(', ');
        setMsg(`Delivers to ${place} by ${eta(days)} · Free shipping · COD available`, 'ok');
      } catch {
        // Offline or the API is down — fall back to the format check rather
        // than blocking the shopper with an error they cannot act on.
        setMsg(`Delivers to ${pin} by ${eta(days)} · Free shipping · COD available`, 'ok');
      } finally {
        button.disabled = false;
      }
    };

    // Look the shopper's location up once, prefill it and run the check, so
    // the delivery estimate is answered before they have to ask. Silent on
    // failure — the field still works by hand.
    const autodetect = async () => {
      if (input.value.trim()) return;

      for (const url of ['https://ipapi.co/json/', 'https://ipwho.is/']) {
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) continue;

          const data = await response.json();
          const country = data.country_code || data.country;
          const postal = String(data.postal || '').trim();
          if (country !== 'IN' && country !== 'India') return;
          if (!/^[1-8]\d{5}$/.test(postal)) continue;

          input.value = postal;
          check();
          return;
        } catch {
          /* Try the next provider. */
        }
      }
    };

    if ('requestIdleCallback' in window) window.requestIdleCallback(autodetect, { timeout: 3000 });
    else setTimeout(autodetect, 1200);

    button.addEventListener('click', check);
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      check();
    });
  }
}

function init() {
  document.querySelectorAll('[data-pdp-buy]').forEach((el) => new ProductBuyBox(el));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
